import { useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useGuild } from '../contexts/GuildContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { 
    Settings as SettingsIcon, 
    MessageSquare,
    Save,
    Tag,
    Plus,
    X
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Settings() {
    const { token } = useAuth();
    const { selectedGuild, guildData, setGuildData } = useGuild();
    const [saving, setSaving] = useState(false);
    const [settings, setSettings] = useState({
        welcome_message: guildData?.welcome_message || '',
        close_message: guildData?.close_message || '',
        categories: guildData?.categories || []
    });
    const [newCategory, setNewCategory] = useState({ name: '', emoji: '' });

    const saveSettings = async () => {
        setSaving(true);
        try {
            const response = await axios.patch(`${API}/guilds/${selectedGuild}/settings`, settings, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setGuildData(response.data);
            toast.success('Settings saved successfully');
        } catch (error) {
            console.error('Error saving settings:', error);
            toast.error('Failed to save settings');
        } finally {
            setSaving(false);
        }
    };

    const addCategory = () => {
        if (!newCategory.name) {
            toast.error('Category name is required');
            return;
        }
        
        const id = newCategory.name.toLowerCase().replace(/\s+/g, '_');
        if (settings.categories.some(c => c.id === id)) {
            toast.error('Category already exists');
            return;
        }
        
        setSettings({
            ...settings,
            categories: [
                ...settings.categories,
                { id, name: newCategory.name, emoji: newCategory.emoji || '📝' }
            ]
        });
        setNewCategory({ name: '', emoji: '' });
    };

    const removeCategory = (id) => {
        setSettings({
            ...settings,
            categories: settings.categories.filter(c => c.id !== id)
        });
    };

    return (
        <div className="space-y-6" data-testid="settings-page">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold">Settings</h1>
                    <p className="text-muted-foreground">Configure your ticket bot settings</p>
                </div>
                
                <Button onClick={saveSettings} disabled={saving} data-testid="save-settings-btn">
                    <Save className="w-4 h-4 mr-2" />
                    {saving ? 'Saving...' : 'Save Changes'}
                </Button>
            </div>

            <div className="grid gap-6">
                {/* Messages */}
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <MessageSquare className="w-5 h-5 text-primary" />
                            Custom Messages
                        </CardTitle>
                        <CardDescription>
                            Customize the messages sent when tickets are opened or closed
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        <div className="space-y-2">
                            <Label htmlFor="welcome_message">Welcome Message</Label>
                            <Textarea
                                id="welcome_message"
                                placeholder="Message sent when a ticket is opened..."
                                value={settings.welcome_message}
                                onChange={e => setSettings({ ...settings, welcome_message: e.target.value })}
                                rows={3}
                                data-testid="welcome-message-input"
                            />
                            <p className="text-xs text-muted-foreground">
                                This message is sent when a new ticket is created
                            </p>
                        </div>
                        
                        <Separator />
                        
                        <div className="space-y-2">
                            <Label htmlFor="close_message">Close Message</Label>
                            <Textarea
                                id="close_message"
                                placeholder="Message sent when a ticket is closed..."
                                value={settings.close_message}
                                onChange={e => setSettings({ ...settings, close_message: e.target.value })}
                                rows={3}
                                data-testid="close-message-input"
                            />
                            <p className="text-xs text-muted-foreground">
                                This message is sent when a ticket is closed
                            </p>
                        </div>
                    </CardContent>
                </Card>

                {/* Categories */}
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Tag className="w-5 h-5 text-primary" />
                            Ticket Categories
                        </CardTitle>
                        <CardDescription>
                            Define categories for organizing tickets
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {/* Existing Categories */}
                        <div className="flex flex-wrap gap-2">
                            {settings.categories.map(category => (
                                <motion.div
                                    key={category.id}
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    className="flex items-center gap-2 px-3 py-2 rounded-lg bg-muted"
                                    data-testid={`category-${category.id}`}
                                >
                                    <span>{category.emoji}</span>
                                    <span className="font-medium">{category.name}</span>
                                    <button
                                        onClick={() => removeCategory(category.id)}
                                        className="ml-1 text-muted-foreground hover:text-destructive transition-colors"
                                        data-testid={`remove-category-${category.id}`}
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                </motion.div>
                            ))}
                        </div>
                        
                        {/* Add New Category */}
                        <div className="flex gap-2 pt-4 border-t">
                            <Input
                                placeholder="Category name"
                                value={newCategory.name}
                                onChange={e => setNewCategory({ ...newCategory, name: e.target.value })}
                                className="flex-1"
                                data-testid="new-category-name"
                            />
                            <Input
                                placeholder="Emoji"
                                value={newCategory.emoji}
                                onChange={e => setNewCategory({ ...newCategory, emoji: e.target.value })}
                                className="w-20"
                                data-testid="new-category-emoji"
                            />
                            <Button onClick={addCategory} data-testid="add-category-btn">
                                <Plus className="w-4 h-4" />
                            </Button>
                        </div>
                    </CardContent>
                </Card>

                {/* Server Info */}
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <SettingsIcon className="w-5 h-5 text-primary" />
                            Server Information
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                                <span className="text-muted-foreground">Server ID</span>
                                <p className="font-mono">{guildData?.guild_id}</p>
                            </div>
                            <div>
                                <span className="text-muted-foreground">Server Name</span>
                                <p>{guildData?.name}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
